import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { usePluginspace } from "../../hooks/usePluginspace";
import { ModalSelectHotel } from "../ModalSelectHotel";

interface HotelOption {
  pluginspaceId: number;
  name: string;
  subdomain: string;
  image?: string;
  description?: string;
  storesHotel?: {
    paymentIntegrated?: boolean;
  };
  paymentIntegrated?: boolean;
}

export default function HotelRedirectHandler(): JSX.Element | null {
  const navigate = useNavigate();
  const { getToken, pluginspace } = usePluginspace();
  const [hotelOptions, setHotelOptions] = useState<HotelOption[]>([]);
  const [showModal, setShowModal] = useState(false);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const run = async () => {
      // Evita reprocessamento se já foi executado
      if (hasProcessed.current) return;

      // Verificar se já existe contexto salvo
      const existingContext = sessionStorage.getItem("hotelReservationContext");
      if (existingContext) {
        hasProcessed.current = true;
        return;
      }

      // Verificar se pluginspace está disponível
      if (!pluginspace?.id) {
        return; // Aguardar pluginspace carregar
      }

      // IMPORTANTE: Setar flag IMEDIATAMENTE antes de qualquer operação assíncrona
      hasProcessed.current = true;

      const params = new URLSearchParams(window.location.search);

      // Only accept explicit reservation + father (pluginspace) and guest token
      const reservation =
        params.get("reservation") || params.get("reservationId") || null;
      const father =
        params.get("father") ||
        params.get("pluginspace") ||
        params.get("pluginspaceId") ||
        null;
      const integration = params.get("integration") || null;
      const token = await getToken();

      // Se vier apenas father (sem reservation), salvar contexto view-only
      // if (!reservation && father && token && !integration) {
      //   try {
      //     // Verificar se subdomain veio na URL
      //     let subdomain =
      //       params.get("subdomain") || params.get("hotel_subdomain") || "";

      //     // Se não veio subdomain na URL, buscar do pluginspace
      //     if (!subdomain) {
      //       try {
      //         const pluginspaceResp = await api.get(
      //           `/api/pluginspace/${father}`,
      //           { headers: { Authorization: `Bearer ${token}` } }
      //         );

      //         if (pluginspaceResp && pluginspaceResp.data) {
      //           // Resposta vem como: { data: [...], success: true }
      //           const rawData =
      //             pluginspaceResp.data.data || pluginspaceResp.data;
      //           const pluginspaceData = Array.isArray(rawData)
      //             ? rawData[0]
      //             : rawData;
      //           subdomain = pluginspaceData?.subdomain || "";
      //         }
      //       } catch (err) {
      //         console.warn("Could not fetch pluginspace subdomain", err);
      //       }
      //     }

      //     const viewOnlyContext = {
      //       pluginspaceId: father,
      //       viewOnly: true, // Flag para indicar que não deve ser enviado ao backend
      //       subdomain: subdomain, // Subdomain para permitir navegação de volta
      //     };

      //     sessionStorage.setItem(
      //       "hotelReservationContext",
      //       JSON.stringify(viewOnlyContext)
      //     );

      //     hasProcessed.current = true;

      //     // Limpar parâmetros da URL
      //     params.delete("father");
      //     params.delete("pluginspace");
      //     params.delete("pluginspaceId");
      //     params.delete("subdomain");
      //     params.delete("hotel_subdomain");
      //     params.delete("integration");

      //     const newSearch = params.toString();
      //     const newUrl =
      //       window.location.pathname +
      //       (newSearch ? `?${newSearch}` : "") +
      //       window.location.hash;
      //     window.history.replaceState({}, document.title, newUrl);
      //   } catch (e) {
      //     console.warn("Could not save view-only hotel context", e);
      //   }
      //   return;
      // }

      if (father && !reservation && token && !integration) {
        try {
          const pluginspaceResp = await api.post(
            `/api/store-relations/parents`,
            {
              child: pluginspace.id,
              hasLink: true,
              father: father ? parseInt(father) : undefined,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (pluginspaceResp?.data?.data) {
            const hotels: HotelOption[] = pluginspaceResp.data.data.map(
              (item: any) => ({
                pluginspaceId: item.parentPluginspace.id,
                name: item.parentPluginspace.name,
                subdomain: item.parentPluginspace.subdomain,
                image: item.storesHotel?.img || "",
                description: item.parentPluginspace.type || "",
                paymentIntegrated: item.storesHotel?.paymentIntegrated || false,
              })
            );

            // Se vier apenas 1 hotel, selecionar automaticamente
            if (hotels.length === 1) {
              const selectedHotel = hotels[0];
              const viewOnlyContext = {
                pluginspaceId: selectedHotel.pluginspaceId,
                viewOnly: !selectedHotel.paymentIntegrated,
                subdomain: selectedHotel.subdomain,
                hotelSelectedByUser: true,
              };

              sessionStorage.setItem(
                "hotelReservationContext",
                JSON.stringify(viewOnlyContext)
              );
            } else if (hotels.length > 1) {
              setHotelOptions(hotels);
              setShowModal(true);
            }
          }
        } catch (e) {
          console.warn("Could not fetch parent hotels with father param", e);
        }
        return;
      }

      // Processar quando vier reservation + father
      if (!reservation || !father) {
        return;
      }

      try {
        // Call server POST find-by-id with body { id, pluginspaceId }
        const resp = await api.post(
          "/api/hotel-reservation/find-by-id",
          { reservationId: reservation, pluginspaceId: father },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (resp && resp.data) {
          // pick only minimal fields
          const raw = resp.data.data ? resp.data.data : resp.data;
          const candidate = Array.isArray(raw) ? raw[0] : raw;
          const pick = (ctx: any) => {
            if (!ctx || typeof ctx !== "object") return undefined;
            const out: any = {};

            // reservation id
            if (ctx.id) out.reservationId = ctx.id;

            // customer document: prefer customer.doc, fallback to cpf-like fields
            const customerDoc =
              ctx.customer?.doc ||
              ctx.customer?.cpf ||
              ctx.customer_doc ||
              ctx.customerDoc ||
              ctx.cpf;
            if (customerDoc) out.doc = customerDoc;

            if (father) out.pluginspaceId = father;

            const subdomain = ctx.pluginspace.subdomain;
            if (subdomain) out.subdomain = subdomain;

            // room id: prefer nested room.id, fallback to top-level roomId/room_id
            const roomId = ctx.room?.id ?? ctx.roomId ?? ctx.room_id;
            if (roomId) out.roomId = roomId;

            if (integration === "false" || !integration) {
              out.viewOnly = true;
            }

            return Object.keys(out).length > 0 ? out : undefined;
          };

          const minimal = pick(candidate.dados[0]);
          if (minimal) {
            // Save to sessionStorage (frontend fallback)
            try {
              sessionStorage.setItem(
                "hotelReservationContext",
                JSON.stringify(minimal)
              );
            } catch (e) {
              console.warn(
                "Could not save hotelReservationContext to sessionStorage",
                e
              );
            }

            // server-side session endpoint not present; only persist to sessionStorage
          }
        }
      } catch (err: unknown) {
        const msg =
          err && typeof err === "object" && "message" in err
            ? (err as any).message
            : String(err);
        console.warn("hotel context save failed", msg);
      }

      // remove only the token/reservation/father params to avoid leaking them
      params.delete("guest_token");
      params.delete("token_id");
      params.delete("token");
      params.delete("reservation");
      params.delete("reservationId");
      params.delete("father");
      params.delete("pluginspace");
      params.delete("pluginspaceId");
      params.delete("integration");

      const newSearch = params.toString();
      // Do NOT modify the path; keep any path segment intact.
      const newUrl =
        window.location.pathname +
        (newSearch ? `?${newSearch}` : "") +
        window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    };

    run().catch((e) => {
      console.warn("HotelRedirectHandler error", e);
      // Reset flag em caso de erro para permitir retry
      hasProcessed.current = false;
    });
  }, [navigate, getToken, pluginspace?.id]);

  const handleHotelSelect = (hotel: HotelOption) => {
    const viewOnlyContext = {
      pluginspaceId: hotel.pluginspaceId,
      viewOnly: !hotel.paymentIntegrated,
      subdomain: hotel.subdomain,
      hotelSelectedByUser: true,
    };

    sessionStorage.setItem(
      "hotelReservationContext",
      JSON.stringify(viewOnlyContext)
    );

    console.log("Hotel selecionado pelo usuário:", hotel);
    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Só renderiza o modal se showModal for true
  if (!showModal) {
    return null;
  }

  return (
    <ModalSelectHotel
      isOpen={showModal}
      hotels={hotelOptions}
      onSelect={handleHotelSelect}
      onClose={handleCloseModal}
    />
  );
}
